import { model, Model, prop, modelAction } from "mobx-keystone"
import { computed } from "mobx"
import { guidesChunk, searchChunk } from "../../docs/lazy"
import type { GuideDoc } from "../../docs/types"

const CATEGORY_ORDER = [
  "quickstart",
  "woocommerce",
  "btcpay",
  "agents",
  "agent-directions",
  "guide",
  "security",
  "operations",
  "reference",
  "recipe"
]

// The two agent-directions pages carry the "quickstart" category upstream, but
// on the index they are a different kind of thing from a quickstart a person
// reads — they are the payload behind the copy button — so they get their own
// group rather than sitting between Node and Rails.
const groupOf = (guide: GuideDoc): string =>
  guide.kind === "agent-directions" ? "agent-directions" : guide.category

@model("or/GuidesBrowser")
export class GuidesBrowser extends Model({
  slug: prop<string | null>(null),
  query: prop<string>("")
}) {
  // Every guide's markdown is its own chunk (docs/lazy). Reading `current` or
  // the index starts loading it; until it lands the page shows a loader.
  @computed
  get loading() {
    return guidesChunk.current == null
  }

  @computed
  get current() {
    if (!this.slug) return null
    return guidesChunk.current?.bySlug[this.slug] ?? null
  }

  // null shows every guide: an empty query, and also the moment before the
  // search chunk arrives, so the list never flashes "No guides matched".
  @computed
  get matchingSlugs() {
    if (!this.query.trim()) return null
    return searchChunk.current?.matchingGuideSlugs(this.query) ?? null
  }

  @computed
  get visibleGuides() {
    const slugs = this.matchingSlugs
    const guides = guidesChunk.current?.publicGuides ?? []
    return guides.filter((guide) => (slugs ? slugs.has(guide.slug) : true))
  }

  @computed
  get groupedGuides() {
    const groups = new Map<string, GuideDoc[]>()
    for (const guide of this.visibleGuides) {
      const group = groupOf(guide)
      const list = groups.get(group) ?? []
      list.push(guide)
      groups.set(group, list)
    }
    return CATEGORY_ORDER.filter((category) => groups.has(category)).map((category) => ({
      category,
      guides: groups.get(category) ?? []
    }))
  }

  // Only once the guides have loaded: before that every slug looks unknown.
  @computed
  get missing() {
    return this.slug != null && !this.loading && this.current == null
  }

  @modelAction
  setSlug = (slug: string | null) => {
    this.slug = slug
  }

  @modelAction
  setQuery = (query: string) => {
    this.query = query
  }
}
